/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  assertUnreachable,
  CancellationToken,
  OperationAlternative,
  OperationFail,
  OperationOk,
  OperationResult,
} from "@gnu-taler/taler-util";
import { useNotificationContext } from "../context/notification.js";
import { useTranslationContext } from "../context/translation.js";
import { AsyncAction, useAsyncAction } from "./useAsyncAction.js";

export type OperationSuccess<Result> =
  Result extends OperationOk<infer Body> ? Body : never;
export type OperationFailure<Result> =
  | (Result extends OperationFail<unknown> ? Result : never)
  | (Result extends OperationAlternative<unknown, unknown> ? Result : never);

export interface NotifiedOperationOptions<
  Result extends OperationResult<unknown, unknown>,
  Args extends unknown[],
> {
  onSuccess?: (
    result: OperationSuccess<Result>,
    ...args: Args
  ) => void | Promise<void>;
  onFail?: (
    failure: OperationFailure<Result>,
    ...args: Args
  ) => void | Promise<void>;
}

/** Connect an OperationResult-returning action to the notification system. */
export function useNotifiedOperation<
  Result extends OperationResult<unknown, unknown>,
  Args extends unknown[],
>(
  operation: (token: CancellationToken, ...args: Args) => Promise<Result>,
  options: NotifiedOperationOptions<Result, Args> = {},
): AsyncAction<Args> {
  const { displayError } = useNotificationContext();
  const { i18n } = useTranslationContext();

  return useAsyncAction(operation, {
    onResult: async (result, ...args) => {
      switch (result.type) {
        case "ok":
          await options.onSuccess?.(
            result.body as OperationSuccess<Result>,
            ...args,
          );
          return;
        case "fail":
          if (options.onFail) {
            await options.onFail(result as OperationFailure<Result>, ...args);
          } else {
            displayError(
              i18n.str`The operation failed.`,
              result,
              i18n.str`The server rejected the operation without a specific error message.`,
            );
          }
          return;
        default:
          assertUnreachable(result);
      }
    },
    onError: (error, ..._args) => {
      console.error("Unexpected error while running an operation", error);
      displayError(
        i18n.str`Unexpected error.`,
        error,
        i18n.str`The runtime threw an Error which was not properly handled. Please report this at https://bugs.taler.net.`,
      );
    },
  });
}
